import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  VStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Button,
  Tab,
  TabList,
  Tabs,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type ColumnSizingState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import * as XLSX from 'xlsx'
import { FileItem } from '@/types'
import apiService from '@/services/api'

interface SpreadsheetViewerProps {
  file: FileItem
  scale: number
  onScaleChange: (scale: number) => void
}

/** Excel-style column label: 0 → A, 25 → Z, 26 → AA */
function excelColumnLabel(index: number): string {
  let label = ''
  let n = index
  while (n >= 0) {
    label = String.fromCharCode((n % 26) + 65) + label
    n = Math.floor(n / 26) - 1
  }
  return label
}

function formatCellValue(v: unknown): string {
  if (v == null || v === '') return ''
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  if (v instanceof Date) return v.toLocaleString()
  return String(v)
}

function sheetToRowData(sheet: XLSX.WorkSheet): {
  rows: Record<string, string>[]
  colCount: number
} {
  const raw = XLSX.utils.sheet_to_json<(unknown)[]>(sheet, {
    header: 1,
    defval: '',
    blankrows: true,
  }) as unknown[][]

  if (!raw.length) {
    return { rows: [], colCount: 0 }
  }

  let colCount = 0
  for (const r of raw) {
    if (Array.isArray(r)) colCount = Math.max(colCount, r.length)
  }
  if (colCount === 0) return { rows: [], colCount: 0 }

  const rows: Record<string, string>[] = raw.map((r) => {
    const row: Record<string, string> = {}
    const arr = Array.isArray(r) ? r : []
    for (let c = 0; c < colCount; c++) {
      row[`c${c}`] = formatCellValue(arr[c])
    }
    return row
  })

  return { rows, colCount }
}

function columnStorageKey(fileId: number, sheetName: string): string {
  return `spreadsheet-cols-${fileId}-${encodeURIComponent(sheetName)}`
}

/** Base row height at scale 1; scales with preview zoom (Chakra `sm` table + line-height). */
const ROW_HEIGHT_BASE_PX = 36

export default function SpreadsheetViewer({ file, scale }: SpreadsheetViewerProps) {
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null)
  const [activeSheet, setActiveSheet] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    setWorkbook(null)
    setActiveSheet(0)

    const load = async () => {
      try {
        const url = apiService.getFilePreviewUrl(file.id)
        const response = await fetch(url)
        if (!response.ok) throw new Error('Failed to fetch file')
        const arrayBuffer = await response.arrayBuffer()
        const wb = XLSX.read(arrayBuffer, { type: 'array' })
        if (!cancelled) {
          setWorkbook(wb)
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load spreadsheet')
          setIsLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [file.id])

  const sheetName = workbook?.SheetNames[activeSheet] ?? ''
  const sheet = workbook && sheetName ? workbook.Sheets[sheetName] : null

  const { rows, colCount } = useMemo(() => {
    if (!sheet) return { rows: [] as Record<string, string>[], colCount: 0 }
    return sheetToRowData(sheet)
  }, [sheet])

  useEffect(() => {
    if (!sheetName) {
      setColumnSizing({})
      return
    }
    try {
      const raw = sessionStorage.getItem(columnStorageKey(file.id, sheetName))
      setColumnSizing(raw ? (JSON.parse(raw) as ColumnSizingState) : {})
    } catch {
      setColumnSizing({})
    }
  }, [file.id, sheetName])

  useEffect(() => {
    if (!sheetName) return
    try {
      sessionStorage.setItem(columnStorageKey(file.id, sheetName), JSON.stringify(columnSizing))
    } catch {
      /* ignore quota / private mode */
    }
  }, [file.id, sheetName, columnSizing])

  const columns = useMemo<ColumnDef<Record<string, string>, string>[]>(() => {
    return Array.from({ length: colCount }, (_, i) => ({
      id: `c${i}`,
      accessorKey: `c${i}`,
      header: excelColumnLabel(i),
      size: 120,
      minSize: 48,
      maxSize: 900,
      cell: (info) => info.getValue() ?? '',
    }))
  }, [colCount])

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    onColumnSizingChange: setColumnSizing,
    state: { columnSizing },
    defaultColumn: {
      minSize: 48,
      maxSize: 900,
    },
  })

  const tableScrollParentRef = useRef<HTMLDivElement>(null)
  const rowModelRows = table.getRowModel().rows

  const rowVirtualizer = useVirtualizer({
    count: rowModelRows.length,
    getScrollElement: () => tableScrollParentRef.current,
    estimateSize: () => Math.round(ROW_HEIGHT_BASE_PX * scale),
    overscan: 10,
  })

  const handleRetry = useCallback(() => {
    setIsLoading(true)
    setError(null)
    setWorkbook(null)
    const load = async () => {
      try {
        const url = apiService.getFilePreviewUrl(file.id)
        const response = await fetch(url)
        if (!response.ok) throw new Error('Failed to fetch file')
        const arrayBuffer = await response.arrayBuffer()
        setWorkbook(XLSX.read(arrayBuffer, { type: 'array' }))
        setIsLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load spreadsheet')
        setIsLoading(false)
      }
    }
    void load()
  }, [file.id])

  if (isLoading) {
    return (
      <Center h="full">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.500">Loading spreadsheet...</Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Center h="full">
        <Alert status="error" maxW="md" borderRadius="lg">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="semibold">Failed to load spreadsheet</Text>
            <Text fontSize="sm">{error}</Text>
            <Button size="sm" onClick={handleRetry}>
              Retry
            </Button>
          </VStack>
        </Alert>
      </Center>
    )
  }

  if (!workbook) return null

  return (
    <Box h="full" display="flex" flexDirection="column" minH={0}>
      <Box
        flex={1}
        minH={0}
        overflow="hidden"
        pt={12}
        bg="white"
        className="ph-no-capture file-content-private"
        data-private="true"
        display="flex"
        flexDirection="column"
      >
        {colCount === 0 ? (
          <Center flex={1}>
            <Text color="gray.500">This sheet is empty.</Text>
          </Center>
        ) : (
          <Box
            ref={tableScrollParentRef}
            flex={1}
            minH={0}
            overflow="auto"
            sx={{
              fontSize: `${14 * scale}px`,
              lineHeight: 1.5,
            }}
          >
            <Table
              size="sm"
              sx={{
                display: 'block',
                width: table.getTotalSize(),
                minWidth: '100%',
                fontFamily: 'mono',
                tableLayout: 'fixed',
              }}
            >
              <Thead
                position="sticky"
                top={0}
                zIndex={1}
                bg="gray.50"
                boxShadow="0 1px 0 0 var(--chakra-colors-gray-200)"
                display="table"
                w={`${table.getTotalSize()}px`}
                minW="100%"
                sx={{ tableLayout: 'fixed' }}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        position="relative"
                        borderColor="gray.200"
                        color="gray.800"
                        fontWeight="semibold"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        style={{
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          maxWidth: header.getSize(),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        <Box
                          position="absolute"
                          right={0}
                          top={0}
                          bottom={0}
                          w="10px"
                          cursor="col-resize"
                          userSelect="none"
                          sx={{ touchAction: 'none' }}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          _hover={{ bg: 'brand.400', opacity: 0.4 }}
                          aria-hidden
                        />
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody
                display="block"
                position="relative"
                h={`${rowVirtualizer.getTotalSize()}px`}
                w={`${table.getTotalSize()}px`}
                minW="100%"
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const row = rowModelRows[virtualRow.index]
                  if (!row) return null
                  return (
                    <Tr
                      key={row.id}
                      position="absolute"
                      top={0}
                      left={0}
                      w="100%"
                      display="table"
                      sx={{
                        tableLayout: 'fixed',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                        _hover: { bg: 'blue.50' },
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Td
                          key={cell.id}
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          style={{
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                      ))}
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {workbook.SheetNames.length > 1 && (
        <Tabs
          index={activeSheet}
          onChange={setActiveSheet}
          size="sm"
          variant="unstyled"
          flexShrink={0}
          bg="gray.50"
          borderTopWidth="1px"
          borderTopColor="gray.200"
        >
          <TabList overflowX="auto" flexWrap="nowrap" minH="40px" alignItems="flex-end" gap={0}>
            {workbook.SheetNames.map((name, idx) => (
              <Tab
                key={idx}
                whiteSpace="nowrap"
                fontSize="sm"
                px={4}
                py={2}
                borderRadius={0}
                mb="-1px"
                color="gray.600"
                borderBottomWidth="1px"
                borderBottomColor="transparent"
                _selected={{
                  color: 'brand.600',
                  bg: 'white',
                  borderTopWidth: '2px',
                  borderTopColor: 'brand.500',
                  borderBottomColor: 'white',
                  fontWeight: 'semibold',
                }}
                _hover={{ color: 'gray.800', bg: 'whiteAlpha.800' }}
              >
                {name}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}
    </Box>
  )
}
